import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import {
  Shield, AlertTriangle, Server, CheckSquare,
  Activity, Briefcase, ClipboardCheck, Archive,
  Clock, FileText, ChevronRight, CheckCircle2,
  Bug, ShieldCheck, Lock
} from 'lucide-react'
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Cell, PieChart, Pie, Legend
} from 'recharts'
import { Link } from 'react-router-dom'

function getRiskColor(score) {
  if (score >= 20) return 'var(--risk-critical)'
  if (score >= 12) return 'var(--risk-high)'
  if (score >= 6) return 'var(--risk-medium)'
  return 'var(--risk-low)'
}

export function Dashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: api.getDashboard,
    refetchInterval: 10000 
  })

  if (isLoading) return <div className="page"><div className="empty-state">Loading dashboard posture…</div></div>

  const stats = data?.stats || {}
  const risks = data?.risks || []
  const annex = data?.annex || []
  const security = data?.security || []
  const pendingDocs = data?.pendingDocs || []

  const vulnerabilities = data?.vulnerabilities || []

  // Compliance Calculation
  const implementedCount = annex.filter(c => c.implementation_status === 'Implemented').length
  const applicableCount = annex.filter(c => c.applicable !== 0).length
  const complianceScore = applicableCount > 0 ? Math.round((implementedCount / applicableCount) * 100) : 0

  const summaryCards = [
    { label: 'Compliance Score', value: `${complianceScore}%`, icon: ShieldCheck, color: 'var(--accent)', sub: 'Annex A Progress' },
    { label: 'Security Controls', value: `${stats.implemented_security_controls || 0} / ${stats.total_security_controls || 0}`, icon: Lock, color: 'var(--success)', sub: 'Implemented' },
    { label: 'Open Vulnerabilities', value: stats.open_vulnerabilities || 0, icon: Bug, color: stats.critical_vulnerabilities > 0 ? 'var(--risk-critical)' : 'var(--warning)', sub: stats.critical_vulnerabilities > 0 ? `${stats.critical_vulnerabilities} Critical` : 'Action Required' },
    { label: 'Pending Risks', value: `${stats.open_risks || 0} / ${stats.total_risks || 0}`, icon: AlertTriangle, color: 'var(--danger)', sub: 'Awaiting Treatment' },
    { label: 'Pending Reviews', value: stats.pending_reviews || 0, icon: ClipboardCheck, color: 'var(--info)', sub: 'Document Library' },
    { label: 'Active Assets', value: stats.total_assets || 0, icon: Server, color: 'var(--text-secondary)', sub: 'Asset Inventory' },
    { label: 'Defined Processes', value: stats.total_processes || 0, icon: Briefcase, color: 'var(--text-secondary)', sub: 'Process Register' },
    { label: 'Total Documents', value: stats.total_documents || 0, icon: Archive, color: 'var(--purple)', sub: 'Document Control' },
  ]

  // Chart Data: Security Controls
  const securityData = [
    { name: 'Implemented', value: stats.implemented_security_controls || 0, color: 'var(--accent)' },
    { name: 'In Progress', value: stats.in_progress_security_controls || 0, color: 'var(--warning)' },
    { name: 'Planned', value: (stats.total_security_controls || 0) - (stats.implemented_security_controls || 0) - (stats.in_progress_security_controls || 0), color: 'var(--text-muted)' },
  ].filter(d => d.value > 0)

  // Chart Data: Annex Implementation
  const annexData = [
    { name: 'Implemented', count: stats.implemented_annex_controls || 0, color: 'var(--accent)' },
    { name: 'Pending', count: stats.pending_annex_controls || 0, color: 'var(--warning)' },
  ]

  // Chart Data: Vulnerabilities
  const vulnData = [
    { name: 'Critical', value: vulnerabilities.filter(v => v.severity === 'Critical' && v.status !== 'Resolved' && v.status !== 'Closed').length, color: 'var(--risk-critical)' },
    { name: 'High', value: vulnerabilities.filter(v => v.severity === 'High' && v.status !== 'Resolved' && v.status !== 'Closed').length, color: 'var(--risk-high)' },
    { name: 'Medium', value: vulnerabilities.filter(v => v.severity === 'Medium' && v.status !== 'Resolved' && v.status !== 'Closed').length, color: 'var(--risk-medium)' },
    { name: 'Low', value: vulnerabilities.filter(v => v.severity === 'Low' && v.status !== 'Resolved' && v.status !== 'Closed').length, color: 'var(--risk-low)' },
  ].filter(d => d.value > 0)

  // Chart Data: Risk Status
  const riskStatusData = [
    { name: 'Open', count: risks.filter(r => r.status === 'Open').length, color: 'var(--danger)' },
    { name: 'In Treatment', count: risks.filter(r => r.status === 'In Progress' || r.status === 'In Treatment').length, color: 'var(--warning)' },
    { name: 'Accepted', count: risks.filter(r => r.status === 'Accepted').length, color: 'var(--info)' },
    { name: 'Closed', count: risks.filter(r => r.status === 'Closed' || r.status === 'Resolved').length, color: 'var(--success)' },
  ].filter(d => d.count > 0)

  return (
    <div className="page">
      <div className="page-header">
        <h1>Governance, Risk & Compliance Dashboard</h1>
        <p>Strategic overview of ISO 27001 implementation, risk posture, and document lifecycle.</p>
      </div>

      <div className="grid-4" style={{ marginBottom: 24 }}>
        {summaryCards.map(c => (
          <div key={c.label} className="stat-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div className="stat-label">{c.label}</div>
                <div className="stat-value" style={{ color: c.color }}>{c.value}</div>
              </div>
              <div style={{ padding: 10, background: c.color + '15', borderRadius: 10 }}>
                <c.icon size={22} color={c.color} />
              </div>
            </div>
            <div className="stat-sub" style={{ marginTop: 8 }}>{c.sub}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 340px', gap: 24, marginBottom: 24 }}>
        {/* Annex Implementation Analytics */}
        <div className="card">
          <div className="flex items-center justify-between" style={{ marginBottom: 20 }}>
            <h3 style={{ fontSize: '0.9375rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>Annex A Implementation</h3>
            <span className="badge badge-neutral">{stats.total_annex_controls || 0} Controls Total</span>
          </div>
          <div style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={annexData} layout="vertical" margin={{ left: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
                <XAxis type="number" hide />
                <YAxis dataKey="name" type="category" stroke="var(--text-secondary)" fontSize={12} />
                <Tooltip 
                  cursor={{ fill: 'var(--bg-elevated)' }}
                  contentStyle={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8 }}
                />
                <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={40}>
                  {annexData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Security Controls Health */}
        <div className="card">
           <h3 style={{ fontSize: '0.9375rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', marginBottom: 20 }}>Security Controls Status</h3>
           <div style={{ height: 280, display: 'flex', alignItems: 'center' }}>
             <ResponsiveContainer width="100%" height="100%">
               <PieChart>
                 <Pie
                   data={securityData}
                   innerRadius={60}
                   outerRadius={80}
                   paddingAngle={5}
                   dataKey="value"
                 >
                   {securityData.map((entry, index) => (
                     <Cell key={`cell-${index}`} fill={entry.color} />
                   ))}
                 </Pie>
                 <Tooltip contentStyle={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8 }} />
                 <Legend verticalAlign="bottom" height={36}/>
               </PieChart>
             </ResponsiveContainer>
           </div>
        </div>

        {/* Pending Reviews Sidebar */}
        <div className="card" style={{ display: 'flex', flexDirection: 'column' }}>
          <h3 style={{ fontSize: '0.9375rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', marginBottom: 16 }}>Pending Reviews</h3>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {pendingDocs.length > 0 ? (
              pendingDocs.map(doc => (
                <Link key={doc.id} to="/review-center" className="nav-item" style={{ margin: 0, padding: '12px', background: 'var(--bg-elevated)', border: '1px solid var(--border)' }}>
                  <FileText size={16} color="var(--accent)" />
                  <div style={{ flex: 1, overflow: 'hidden' }}>
                    <div style={{ fontSize: '0.8125rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{doc.title}</div>
                    <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>Awaiting approval</div>
                  </div>
                  <ChevronRight size={14} color="var(--text-muted)" />
                </Link>
              ))
            ) : (
              <div className="empty-state" style={{ padding: '40px 0' }}>
                <CheckCircle2 size={32} color="var(--accent)" style={{ opacity: 0.5, marginBottom: 8 }} />
                <p style={{ fontSize: '0.8125rem' }}>No pending reviews</p>
              </div>
            )}
          </div>
          {pendingDocs.length > 0 && (
            <Link to="/review-center" className="btn btn-ghost btn-sm" style={{ marginTop: 12, justifyContent: 'center' }}>
              View Review Center
            </Link>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
        {/* Risk Status Breakdown */}
        <div className="card">
          <div className="flex items-center justify-between" style={{ marginBottom: 20 }}>
            <h3 style={{ fontSize: '0.9375rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>Risk Treatment Status</h3>
            <span className="badge badge-neutral">{stats.total_risks || 0} Total Risks</span>
          </div>
          <div style={{ height: 280 }}>
            {riskStatusData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={riskStatusData} layout="vertical" margin={{ left: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
                  <XAxis type="number" hide />
                  <YAxis dataKey="name" type="category" stroke="var(--text-secondary)" fontSize={12} />
                  <Tooltip 
                    cursor={{ fill: 'var(--bg-elevated)' }}
                    contentStyle={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8 }}
                  />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={40}>
                    {riskStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty-state" style={{ height: '100%' }}>No risks found.</div>
            )}
          </div>
        </div>

        {/* Vulnerability Severity Breakdown */}
        <div className="card">
           <div className="flex items-center justify-between" style={{ marginBottom: 20 }}>
             <h3 style={{ fontSize: '0.9375rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>Open Vulnerabilities</h3>
             <span className="badge badge-neutral">{stats.open_vulnerabilities || 0} Pending</span>
           </div>
           <div style={{ height: 280, display: 'flex', alignItems: 'center' }}>
             {vulnData.length > 0 ? (
               <ResponsiveContainer width="100%" height="100%">
                 <PieChart>
                   <Pie
                     data={vulnData}
                     innerRadius={60}
                     outerRadius={80}
                     paddingAngle={5}
                     dataKey="value"
                   >
                     {vulnData.map((entry, index) => (
                       <Cell key={`cell-${index}`} fill={entry.color} />
                     ))}
                   </Pie>
                   <Tooltip contentStyle={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8 }} />
                   <Legend verticalAlign="bottom" height={36}/>
                 </PieChart>
               </ResponsiveContainer>
             ) : (
               <div className="empty-state" style={{ height: '100%', width: '100%' }}>
                 <CheckCircle2 size={32} color="var(--success)" style={{ opacity: 0.5, marginBottom: 8 }} />
                 <p>No open vulnerabilities</p>
               </div>
             )}
           </div>
        </div>
      </div>

      {/* Critical Risks Section */}
      <div className="card">
        <div className="flex items-center justify-between" style={{ marginBottom: 16 }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600 }}>Top Risks Needing Mitigation</h3>
          <Link to="/risks" className="btn btn-ghost btn-sm">View Risk Register</Link>
        </div>
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Risk Scenario</th>
                <th>Score</th>
                <th>Residual</th>
                <th>Treatment</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {risks.filter(r => r.risk_score >= 12).slice(0, 5).map(r => (
                <tr key={r.id}>
                  <td style={{ fontWeight: 600 }}>{r.description}</td>
                  <td><span style={{ fontWeight: 700, color: getRiskColor(r.risk_score) }}>{r.risk_score}</span></td>
                  <td><span style={{ fontWeight: 700, color: getRiskColor(r.residual_risk) }}>{r.residual_risk?.toFixed(1)}</span></td>
                  <td><span className="badge badge-neutral">{r.treatment_type}</span></td>
                  <td>
                    <span className={`badge ${r.status === 'Open' ? 'badge-danger' : r.status === 'In Progress' ? 'badge-warning' : 'badge-success'}`}>
                      {r.status}
                    </span>
                  </td>
                </tr>
              ))}
              {risks.filter(r => r.risk_score >= 12).length === 0 && (
                <tr><td colSpan={5} className="text-center text-muted" style={{ padding: 20 }}>No critical risks requiring immediate attention.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
